import API from 'services/API';
import { ERROR_CONSTANTS } from 'constants';
import logger from 'lib/common/logger';
import features from 'lib/common/features';
import combineSubLineItems from 'lib/cart/combineSubLineItems';

import get from 'lodash/get';
import head from 'lodash/head';
import includes from 'lodash/includes';
import partition from 'lodash/partition';
import uniq from 'lodash/uniq';
import castArray from 'lodash/castArray';
import isObject from 'lodash/isObject';

function normalizeErrorCode(errResponse, fallbackMessage) {
  if (
    errResponse.error.indexOf(
      'One or more of the selected seats is not available'
    ) !== -1
  ) {
    return ERROR_CONSTANTS.SEATS_UNAVAILABLE;
  }

  if (
    errResponse.error.indexOf(
      'This seat combination would leave single seats'
    ) !== -1
  ) {
    return ERROR_CONSTANTS.LEAVES_SINGLE_SEAT;
  }

  if (
    errResponse.error.indexOf('This request will exceed the ticket limit') !==
    -1
  ) {
    return 'You have exceeded the ticket limit, please reduce your order to proceed.';
  }

  return fallbackMessage;
}

export function getCartLineItems(cart) {
  var lineSeqNumbers = cart.map(x => x.li_seq_no);
  return uniq(lineSeqNumbers);
}

function filterCartItemsByPriceTypes(cartItems, priceTypes) {
  return cartItems.filter(function(item) {
    return includes(priceTypes, parseInt(item.price_type, 10));
  });
}

function filterLineItemsByPriceTypes(lineItems, priceTypes) {
  return lineItems.filter(function(item) {
    return includes(priceTypes, parseInt(item.priceData.price_type, 10));
  });
}

export function filterRemainingItems(cart, newSeats) {
  var filteredByNewSeats = cart.filter(item => {
    let newSeatIds = newSeats.map(x => x.seatData.seat_no);
    return newSeatIds.indexOf(parseInt(item.seat_no, 10)) !== -1;
  });

  return uniq(filteredByNewSeats).map(item => item.li_seq_no);
}

export function partitionExistingItems(lineItems, cart) {
  return partition(lineItems, item => {
    let cartIds = cart.map(x => parseInt(x.seat_no, 10));
    return cartIds.indexOf(item.seatData.seat_no) === -1;
  });
}

function releaseAll(perfNo, items) {
  return Promise.all(
    items.map(function(item) {
      return API.ReleaseTickets(perfNo, item);
    })
  );
}

function processNewFixedPackage(packageNo, lineItems, leaveSingleSeats) {
  let messages = {
    error: 'There was a problem adding your package.'
  };

  return new Promise((resolve, reject) => {
    API.AddPackageItemSeated(
      {
        pkg_no: packageNo,
        zone_no: 0,
        leaveSingleSeats: leaveSingleSeats
      },
      lineItems
    )
      .then(response => {
        let result;
        if (isObject(response.result)) {
          result = parseInt(response.result[0], 10);
        } else {
          result = parseInt(response.result, 10);
        }

        if (result === lineItems.length) {
          resolve(lineItems);
        } else {
          reject(new Error(messages.error));
        }
      })
      .catch(() => {
        reject(new Error(messages.error));
      });
  });
}

/**
 * Fixed package
 */
function processExistingFixedPackage(
  currentFixedPackage,
  lineItems,
  existingSeats,
  leaveSingleSeats
) {
  let messages = {
    partialError: 'There was a problem amending your seats.',
    removalError: 'There was an error, please try again.'
  };

  return new Promise((resolve, reject) => {
    API.RemovePackageItem(
      currentFixedPackage.pkg_no,
      currentFixedPackage.li_seq_no
    )
      .then(() => {
        API.AddPackageItemSeated(
          currentFixedPackage,
          lineItems,
          leaveSingleSeats
        )
          .then(response => {
            let result;
            if (isObject(response.result)) {
              result = parseInt(response.result[0], 10);
            } else {
              result = parseInt(response.result, 10);
            }

            /**
                 * Failed to reserve all of your seats
                 * but not a tessitura error.
                 */
            if (result === 0) {
              API.AddPackageItemSeated(
                currentFixedPackage,
                existingSeats,
                leaveSingleSeats
              ).then(resolve);
            }

            /**
                 * Added all seats
                 * or, added some but not others.
                 */
            if (result === lineItems.length) {
              resolve(lineItems);
            } else {
              reject(new Error(messages.partialError));
            }
          })
          .catch(() => {
            reject(new Error(messages.partialError));
          });
      })
      .catch(() => {
        reject(new Error(messages.removalError));
      });
  });
}

/**
 * Flex packages
 */
function processFlexPackage(
  perfNo,
  currentFlexPackage,
  lineItems,
  cartItems,
  cart
) {
  var messages = {
    reserveError: 'There was a problem reserving your seats.'
  };

  var filterSettings = features.getOption('filterPriceTypes', 'options');

  var packageLineItems = castArray(get(cart, 'PackageLineItem'));

  // Find matching package line items
  var matchingPackageLineItems = packageLineItems.filter(function(item) {
    return item.li_seq_no === currentFlexPackage.li_seq_no;
  });

  // Flex seats currently in your Tessitura cart
  var currentFlexSeats = filterCartItemsByPriceTypes(
    cartItems,
    filterSettings.priceTypes.flexPackages
  );

  // Current add on seats current in your Tessitura cart
  var currentFlexAddOnSeats = filterCartItemsByPriceTypes(
    cartItems,
    filterSettings.priceTypes.subscriberAddOn
  );

  // Selected flex seats in your SYOS mini-cart
  var selectedFlexSeats = filterLineItemsByPriceTypes(
    lineItems,
    filterSettings.priceTypes.flexPackages
  );

  // Selected add-on seats in your SYOS mini-cart
  var selectedFlexAddOnSeats = filterLineItemsByPriceTypes(
    lineItems,
    filterSettings.priceTypes.subscriberAddOn
  );

  // If there is only one line item in the sequence
  var packageLiNo =
    matchingPackageLineItems.length === 1 ? 0 : currentFlexPackage.pkg_li_no;

  /**
     * Only proceed if we have as many DYO seats in
     * our mini-cart as we did to begin with.
     */
  if (selectedFlexSeats.length === currentFlexSeats.length) {
    return new Promise((resolve, reject) => {
      function handleReservationRejection(err) {
        reject(new Error(messages.reserveError));
        logger.captureException(err);
      }

      /**
             * [1] Drop the existing flex package line item
             * [2] Add newly selected flex package seats
             *      [2a] Get the newly added item from the cart
             *      [2b] Add an order comment using note from existing
             *           parent package line item so we can group them
             *           correctly in the cart
             * [3a] If we have add on seats then we need to process them
             *      [4a] If we have existing add on seats (i.e. we are editing)
             *           then we need to process them like single seat editing.
             *      [4b] We only have new seats
             * [3b] No add on seats so we can just proceed
             */
      API.RemoveNFSPackage(currentFlexPackage.li_seq_no, perfNo)
        .then(() => {
          API.AddNFSPackagePerformanceItem(
            packageLiNo,
            currentFlexPackage.pkg_no,
            currentFlexPackage.perf_group_no,
            selectedFlexSeats,
            perfNo
          )
            .then(flexResponse => {
              if (flexResponse.error) {
                handleReservationRejection(flexResponse.error);
              }

              if (selectedFlexAddOnSeats.length) {
                if (currentFlexAddOnSeats.length) {
                  processAmendSingleSeats(
                    perfNo,
                    selectedFlexAddOnSeats,
                    currentFlexAddOnSeats
                  ).then(resolve);
                } else {
                  processNewSeats(
                    perfNo,
                    selectedFlexAddOnSeats
                  ).then(addOnResponse => {
                    if (addOnResponse.error) {
                      handleReservationRejection(addOnResponse.error);
                    } else {
                      resolve(addOnResponse);
                    }
                  });
                }
              } else {
                resolve(flexResponse);
              }
            })
            .catch(handleReservationRejection);
        })
        .catch(handleReservationRejection);
    });
  } else {
    let priceTypeDescription = get(
      head(selectedFlexSeats),
      'priceData.price_type_desc'
    );
    let msg = `You must select ${currentFlexSeats.length} ${priceTypeDescription} seats.`;
    return Promise.reject(new Error(msg));
  }
}

/**
 * Single Seats
 */
function processAmendSingleSeats(
  perfNo,
  lineItems,
  cartItems,
  leaveSingleSeats
) {
  let [newSeats, existingSeats] = partitionExistingItems(lineItems, cartItems);
  let currentLineItems = getCartLineItems(cartItems);
  let podSeats = get(lineItems, 'podSeats');

  if (podSeats) {
    existingSeats['podSeats'] = podSeats;
  }

  let amountThatShouldBeBooked = newSeats.length;
  if (amountThatShouldBeBooked && podSeats) {
    newSeats['podSeats'] = podSeats;

    newSeats.forEach(seat => {
      podSeats.forEach(pod => {
        if (pod.seats.includes(seat.seatData.seat_no)) {
          amountThatShouldBeBooked += pod.amount - 1;
        }
      });
    });
  }

  let messages = {
    reserveError: 'There was a problem reserving your seats.'
  };

  if (amountThatShouldBeBooked) {
    return new Promise(function(resolve, reject) {
      /**
             * Reserve new seats
             */
      API.ReserveTicketsSpecifiedSeats(perfNo, newSeats, leaveSingleSeats).then(
        response => {
          let result;
          if (isObject(response.result)) {
            result = parseInt(response.result[0], 10);
          } else {
            result = parseInt(response.result, 10);
          }

          if (result === 0) {
            let newErr = new Error('Seats not added');
            newErr.response = response.error;
            reject(newErr);
          }

          if (result === amountThatShouldBeBooked) {
            /**
                     * Release old line items
                     */
            releaseAll(perfNo, currentLineItems)
              .then(() => {
                /**
                         * If we have existing seats from our original line item re-add them
                         */
                if (existingSeats.length) {
                  API.ReserveTicketsSpecifiedSeats(
                    perfNo,
                    existingSeats,
                    leaveSingleSeats
                  )
                    .then(resolve)
                    .catch(() => {
                      reject(new Error(messages.reserveError));
                    });
                } else {
                  resolve(response);
                }
              })
              .catch(() => {
                reject(new Error(messages.reserveError));
              });
          } else {
            API.getCart()
              .then(newCart => {
                let newItems = combineSubLineItems(newCart, perfNo);
                let remainingLineItems = filterRemainingItems(
                  newItems,
                  newSeats
                );
                releaseAll(perfNo, remainingLineItems)
                  .then(resolve)
                  .catch(() => {
                    reject(new Error(messages.reserveError));
                  });
              })
              .catch(() => {
                reject(new Error(messages.reserveError));
              });
          }
        },
        err => {
          err.response.json().then(function(errResponse) {
            reject(
              new Error(normalizeErrorCode(errResponse, messages.reserveError))
            );
          });
        }
      );
    });
  } else {
    return new Promise(function(resolve, reject) {
      releaseAll(perfNo, currentLineItems).then(
        () => {
          API.ReserveTicketsSpecifiedSeats(
            perfNo,
            existingSeats,
            leaveSingleSeats
          ).then(resolve, () => {
            reject(new Error(messages.reserveError));
          });
        },
        () => {
          reject(new Error(messages.reserveError));
        }
      );
    });
  }
}

function processNewSeats(perfNo, selections, leaveSingleSeats) {
  return new Promise((resolve, reject) => {
    API.ReserveTicketsSpecifiedSeats(
      perfNo,
      selections,
      leaveSingleSeats
    ).then(resolve, err => {
      // TODO: is there a way to return the parsed json response upfront?
      err.response.json().then(function(errResponse) {
        reject(
          new Error(
            normalizeErrorCode(
              errResponse,
              'There was a problem reserving your seats.'
            )
          )
        );
      });
    });
  });
}

/**
 * Process Mini Cart
 */
export default function processMiniCart(
  cart,
  cartItems,
  lineItems,
  performanceNo,
  packageNo,
  currentFixedPackage,
  currentFlexPackage,
  leaveSingleSeats
) {
  /**
     * If we don't have an existing cart then this is a
     * new session and we can just reserve tickets.
     */
  if (!cartItems.length) {
    if (packageNo) {
      /**
             * If we are choosing seats for a new package
             */
      return processNewFixedPackage(packageNo, lineItems, leaveSingleSeats);
    } else {
      /**
             * If we are choosing seats for a new performance
             */
      return processNewSeats(performanceNo, lineItems, leaveSingleSeats);
    }
  } else if (currentFixedPackage) {
    /**
         * If we are editing a fixed package performance
         */
    return processExistingFixedPackage(
      currentFixedPackage,
      lineItems,
      leaveSingleSeats
    );
  } else {
    /**
         * If we are editing single seats
         *
         * Note: Editing fixed package seats at the performance level
         *       is included here as package seats are locked and
         *       excluded from processing which lets us treat add on
         *       seats simply like single seats.
         *
         */
    return processAmendSingleSeats(
      performanceNo,
      lineItems,
      cartItems,
      leaveSingleSeats
    );
  }
}



// WEBPACK FOOTER //
// ./src/javascripts/app/lib/cart/processMiniCart.js